import { UserTaskItemStore } from './user-task-item.store';
import { Component, computed, inject, input } from '@angular/core';
import { TagComponent } from '../../../../kit/tag/tag.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { EditUserTaskItemDialogComponent } from '../edit-user-task-item-dialog/edit-user-task-item-dialog.component';
import { translate } from '../../../../../modules/shared/shared.global-service';
import { updateTaskFrag } from '../user-tasks/user-tasks.query';
import { ConfirmBuilder } from '../../../../../modules/shared/service/confirm.builder';
import { contentGlobalSuccess, successDeleted } from '../../../../../modules/content/content.error';
import { UserTaskObjectExtendedStatus } from './user-task-item.types';
import { UserTaskObject } from '@libs/types/src/services/object-type/user/user-task.object';
import { TranslateService } from '@ngx-translate/core';
import { pluralize } from '../../../../kit/pluralization/pluralization.component';
import { localDate } from '../../../../../modules/shared/pipes/local-date.pipe';
import { SessionService } from '../../../../../services/session.service';
import { BadgeDirective } from '@openng/optimus-ui/badge';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { AvatarGroup } from '@openng/optimus-ui/avatargroup';
import { AvatarComponent } from '../../../../../modules/shared/components/kit/avatar/avatar.component';
import { PersonInitialsPipe } from '../../../../../modules/shared/pipes/personInitials';

@Component({
    selector: 'app-user-task-item',
    templateUrl: './user-task-item.component.html',
    styleUrl: './user-task-item.component.scss',
    providers: [UserTaskItemStore],
    imports: [
        TagComponent,
        ButtonComponent,
        BadgeDirective,
        Tooltip,
        AvatarGroup,
        AvatarComponent,
        PersonInitialsPipe,
    ],
})
export class UserTaskItemComponent {

    public readonly store = inject(UserTaskItemStore);
    public readonly dialogService = inject(BaseDialogService);
    public readonly confirm = inject(ConfirmBuilder);
    public readonly translate = inject(TranslateService);
    public readonly session = inject(SessionService);

    public item = input.required<UserTaskObject>();

    protected status = computed(() => {
        return this.statusDetail(this.item());
    });

    protected showDetail() {
        this.dialogService.open(EditUserTaskItemDialogComponent,
            {
                header: translate.instant('userTasks.title.detail'),
                styleClass: 'p-dialog-width-60',
                data: { item: this.item() },
            });
    }

    protected toggleCompleteItem(event: any) {
        const isCompleted = this.item().completedAt;
        const prefix = 'userTasks.item.messages.';

        this.confirm.confirm(
            `${prefix}${isCompleted ? 'activate' : 'complete'}ConfirmationLabel`,
            null,
            async () => {
                await this.store.api.updateUserTask({
                    id: this.item().id,
                    completedAt: isCompleted ? null : (new Date()).toISOString(),
                }, updateTaskFrag);
                contentGlobalSuccess.next(`userTasks.item.${isCompleted ? 'moved' : 'completed'}`);
            },
            null,
            isCompleted ? 'pi-circle' : 'pi-check-circle',
            event?.target,
        );
    }

    protected deleteItem(event: any) {
        this.confirm.confirm(
            'userTasks.item.messages.deleteConfirmationLabel',
            null,
            async () => {
                await this.store.api.deleteUserTask({
                    id: this.item().id,
                });
                successDeleted();
            },
            null,
            'pi-trash',
            event?.target,
        );
    }

    private statusDetail(it: UserTaskObject): UserTaskObjectExtendedStatus | null {
        if (!it.dueDate) return null;

        const date = it.completedAt ? new Date(it.completedAt) : new Date();
        const dueDate = new Date(it.dueDate);
        const diff = Math.floor((date.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)); // in days

        const severity = diff < 0 ? 'success' : 'danger';
        const title = this.translate.instant('userTasks.item.status.title', {
            type: this.translate.instant(`userTasks.item.status.${diff < 0 ? 'in' : 'after'}`),
            daysCount: Math.abs(diff),
            daysInfo: pluralize(Math.abs(diff), `${diff < 0 ? 'in' : 'after'}Day`, this.translate),
            date: localDate(it.completedAt || dueDate, this.session, 'dd.MM.yyyy'),
        });

        return <UserTaskObjectExtendedStatus>{
            severity,
            title,
        };
    }

    protected readonly Array = Array;
}
